import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import './PublicHeader.css';

const NAV_ITEMS = [
  { label: 'ABOUT US',     id: 'about-us',    href: '/#about-us' },
  { label: 'HOW IT WORKS', id: 'how-to-play', href: '/#how-to-play' },
  { label: 'SHOWDOWNS',    id: 'showdowns',   href: '/#showdowns' },
  { label: 'LEADERBOARD',  id: 'leaderboard', href: '/#leaderboard' },
  { label: 'LEAGUE',       id: null,          href: '/league' },
  { label: 'RULES',        id: null,          href: '/rules' },
];

// DOM order of sections (top → bottom on the page) — scroll-spy uses this,
// NOT the nav display order above. Must match the rendered document order:
//   home(hero) → showdowns → leaderboard → about-us(features) → how-to-play
const PAGE_SECTION_ORDER = ['home', 'showdowns', 'leaderboard', 'about-us', 'how-to-play'];
const HEADER_OFFSET = 90;

export const PublicHeader = () => {
  const [activeSection,    setActiveSection]    = useState('home');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // ── Scroll-spy ─────────────────────────────────────────────────────────────
  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      let current = 'home';
      for (const id of PAGE_SECTION_ORDER) {
        const el = document.getElementById(id);
        if (!el) continue;
        const top = el.getBoundingClientRect().top + scrollY - HEADER_OFFSET - 24;
        if (scrollY >= top) current = id;
      }
      setActiveSection(current);
    };
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, []);

  // Close mobile menu when viewport widens past burger breakpoint
  useEffect(() => {
    const onResize = () => { if (window.innerWidth > 1024) setIsMobileMenuOpen(false); };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const closeMenu = () => setIsMobileMenuOpen(false);

  return (
    <header className="ss-header-container">

      {/* Inner container — constrains content to --ss-max-width (1400px)
          while the header background remains full viewport width         */}
      <div className="ss-header-inner">

        {/* ── Oversized logo ───────────────────────────────────────────── */}
        <div className="ss-logo-wrapper">
          <Link to="/" onClick={() => { closeMenu(); window.scrollTo({ top: 0, behavior: 'smooth' }); }}>
            <img src="/logo.png" alt="Syndicator Showdown" className="ss-logo-img" />
          </Link>
        </div>

        {/* ── Brand text — also links home ────────────────────────────── */}
        <Link to="/" onClick={() => { closeMenu(); window.scrollTo({ top: 0, behavior: 'smooth' }); }} className="ss-brand-text">
          <h1 className="ss-brand-title">
            <span className="ss-title-1">SYNDICATOR</span>
            <span className="ss-title-2">SHOWDOWN</span>
          </h1>
          <p className="ss-brand-slogan">OWN THE DREAM. COMPETE FOR GLORY.</p>
        </Link>

        {/* ── Nav (collapses to burger at ≤1300px) ────────────────────── */}
        <nav
          id="ss-main-nav"
          className={`ss-nav-menu${isMobileMenuOpen ? ' open' : ''}`}
          aria-label="Main navigation"
        >
          <ul className="ss-nav-list">
            {NAV_ITEMS.map(({ label, id, href }) => (
              <li key={label}>
                {href.startsWith('/') && !href.includes('#') ? (
                  // Pure-path routes — React Router Link for SPA navigation
                  <Link
                    to={href}
                    className={`ss-nav-link${activeSection === id ? ' active' : ''}`}
                    onClick={closeMenu}
                  >
                    {label}
                  </Link>
                ) : (
                  // Hash links (/#section) — native <a> for reliable cross-page scroll
                  <a
                    href={href}
                    className={`ss-nav-link${activeSection === id ? ' active' : ''}`}
                    onClick={closeMenu}
                  >
                    {label}
                  </a>
                )}
              </li>
            ))}
          </ul>
        </nav>

        {/* ── Controls: LOGIN · JOIN NOW ───────────────────────────────── */}
        <div className="ss-header-controls">
          <Link to="/signin"             className="ss-btn-login">LOGIN</Link>
          <Link to="/signin?mode=signup" className="ss-btn-join">JOIN NOW</Link>
        </div>

        {/* ── Hamburger toggle — last child = far right ─────────────────── */}
        <button
          type="button"
          className="ss-mobile-toggle"
          aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={isMobileMenuOpen}
          aria-controls="ss-main-nav"
          onClick={() => setIsMobileMenuOpen(prev => !prev)}
        >
          {isMobileMenuOpen ? '✕' : '☰'}
        </button>

        {/* ── Thin blue accent line ─────────────────────────────────────── */}
        <div className="ss-header-blue-line" aria-hidden="true" />

      </div>{/* end .ss-header-inner */}
    </header>
  );
};

export default PublicHeader;
